import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowUpRight } from "lucide-react";
import { JHALA_GLOBAL_CSS, TopStrip, Header, Footer, Reveal, TagChip } from "components/jhala/Layout.js";

const FAQS = [
  {
    q: "What services does JhalaTech offer?",
    a: "Ecommerce and Shopify development is our core specialty — store setup, Shopify Plus, headless commerce, migrations, and CRO. Beyond that, we're a full-stack team: website and API development, DevOps support, server management, and tech consulting.",
  },
  {
    q: "How much does a project cost?",
    a: "It depends on scope — a Shopify store setup and a custom MERN platform are priced very differently. Check our Pricing page for starting ranges by project type, or get in touch for a quote tailored to your project.",
  },
  {
    q: "How long does a typical project take?",
    a: "Simple store setups can launch in 1-2 weeks. Custom stores or MVPs typically take 3-6 weeks. Larger platforms and enterprise builds run 8-12+ weeks depending on scope. We'll give you a concrete timeline after the discovery call.",
  },
  {
    q: "Do you only work with Shopify?",
    a: "No — ecommerce is where we go deepest, but we're also full-stack engineers. If a project needs a custom checkout feature, a standalone web app, or infrastructure beyond what Shopify offers, we build that too.",
  },
  {
    q: "Can you migrate my existing store to Shopify?",
    a: "Yes. We handle full migrations from WooCommerce, Magento, or custom platforms, aiming for zero data loss and preserved SEO rankings.",
  },
  {
    q: "Do you provide support after launch?",
    a: "Yes — post-launch support is standard for our clients, not an upsell. We don't hand off a project and disappear; ongoing maintenance and monitoring keep things running.",
  },
  {
    q: "Do you work with startups, or only larger brands?",
    a: "Both. We're startup-friendly and enterprise-capable — from first-time founders launching their first store to funded, high-volume brands scaling on Shopify Plus.",
  },
  {
    q: "What's your refund policy?",
    a: "We don't offer refunds once work has commenced, with limited exceptions handled case by case. Full details are in our Refund Policy.",
  },
  {
    q: "How do we get started?",
    a: "Start with a free discovery call — we learn about your business, goals, and technical needs, then follow up with a clear scope, timeline, and pricing before any work begins.",
  },
  {
    q: "Where is JhalaTech based?",
    a: "We're based in Indore, Madhya Pradesh, India, and work with clients remotely worldwide.",
  },
];

function FaqItem({ item, isOpen, onToggle }) {
  return (
    <div className="faq-item" style={{ border: "1px solid #DCD8CC", borderRadius: 8, background: "#FFFFFF", overflow: "hidden" }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16,
          padding: "18px 22px", background: "none", border: "none", cursor: "pointer", textAlign: "left",
          fontFamily: "'Space Grotesk', sans-serif", fontSize: 15.5, fontWeight: 600, color: "#0A0F1E",
        }}
      >
        {item.q}
        <ChevronDown size={18} style={{ color: "#4CE0B3", flexShrink: 0, transition: "transform 0.2s ease", transform: isOpen ? "rotate(180deg)" : "none" }} />
      </button>
      <div style={{ maxHeight: isOpen ? 300 : 0, overflow: "hidden", transition: "max-height 0.3s ease" }}>
        <p style={{ margin: 0, padding: "0 22px 20px", color: "#6B7280", fontSize: 14, lineHeight: 1.65 }}>{item.a}</p>
      </div>
    </div>
  );
}

export default function FAQsPage() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: "#F6F4EE", color: "#0A0F1E" }}>
      <style>{JHALA_GLOBAL_CSS}</style>
      <TopStrip />
      <Header />

      {/* HERO */}
      <section style={{ background: "#0A0F1E", color: "#F6F4EE", padding: "64px 24px 56px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(#23304A 1px, transparent 1px), linear-gradient(90deg, #23304A 1px, transparent 1px)", backgroundSize: "48px 48px", opacity: 0.2, maskImage: "radial-gradient(ellipse at 70% 30%, black, transparent 70%)" }} />
        <div style={{ maxWidth: 1180, margin: "0 auto", position: "relative" }}>
          <Reveal>
            <TagChip>$ jhalatech --faq</TagChip>
            <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(30px, 4.4vw, 46px)", lineHeight: 1.1, margin: "18px 0 14px" }}>
              Frequently asked questions.
            </h1>
            <p style={{ fontSize: 16, color: "#B8BFCF", maxWidth: 560, lineHeight: 1.65 }}>
              Answers to the things we get asked most. Can't find what you're looking for? Just get in touch.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAQ LIST */}
      <section style={{ padding: "70px 24px 90px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map((item, i) => (
            <Reveal key={item.q} delay={i * 0.03}>
              <FaqItem item={item} isOpen={openIndex === i} onToggle={() => setOpenIndex(openIndex === i ? -1 : i)} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "70px 24px", background: "#0A0F1E", color: "#F6F4EE", textAlign: "center" }}>
        <Reveal>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(22px, 3vw, 30px)", fontWeight: 700, margin: "0 0 14px" }}>
            Still have questions?
          </h2>
          <p style={{ color: "#B8BFCF", fontSize: 15, marginBottom: 26 }}>
            We're happy to talk through your specific project.
          </p>
          <Link to="/contact-us" className="cta-btn" style={{ background: "#4CE0B3", color: "#0A0F1E", border: "none", padding: "13px 24px", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: "pointer", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
            Get In Touch <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
